import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { IoArrowBack, IoCheckmarkCircle, IoRocketOutline } from 'react-icons/io5';
import { PROPACK_DEFAULT_ITEMS, PROPACK_PRIMARY_OPTIONS, PROPACK_SIZE_OPTIONS, PROPACK_SECONDARY_OPTIONS_MAPPING } from '../../../data/conservativeCareDashboard';
import KitDurationSelection from './KitDurationSelection';
import sheetimage from '../../../assets/images/collagen-sheet.png';
import powderimage from '../../../assets/images/collagen-powder.jpg';

const ProPackBuilder = ({
  orderStep,
  setOrderStep,
  selectedPatient,
  woundDetails,
  proPackPrimary,
  setProPackPrimary,
  proPackPrimarySize,
  setProPackPrimarySize,
  proPackSecondary,
  setProPackSecondary,
  proPackSecondarySize,
  setProPackSecondarySize,
  kitDuration,
  setKitDuration,
  ivrSubmitted,
  setOpenIvrModal,
  fetchDeliveryAddress,
  setDeliveryModalOpen,
  loading
}) => {
  // Reset secondary selection when prerequisites change
  useEffect(() => {
    setProPackSecondary('');
    setProPackSecondarySize('');
  }, [kitDuration, woundDetails.drainage, proPackPrimarySize, setProPackSecondary, setProPackSecondarySize]);

  // Get available secondary options based on configuration
  const getSecondaryOptions = () => {
    if (!kitDuration || !woundDetails.drainage || !proPackPrimarySize) {
      return [];
    }
    return PROPACK_SECONDARY_OPTIONS_MAPPING[kitDuration]?.[woundDetails.drainage]?.[proPackPrimarySize] || [];
  };

  const secondaryOptions = getSecondaryOptions();

  const canSubmit = () => {
    if (loading) return false;
    if (!proPackPrimary) return false;
    if (proPackPrimary === 'collagen_sheet' && !proPackPrimarySize) return false;
    if (proPackPrimary === 'collagen_powder' && !proPackPrimarySize) return false;
    if (!proPackSecondary) return false;
    return true;
  };

  // Add this function inside the ProPackBuilder component, before the return statement
const getFilteredDefaultItems = () => {
  // Check if secondary dressing has a border (exclude items with "no_border" or "abd_pad")
  const hasBorder = proPackSecondary && 
    proPackSecondary.includes('border') && 
    !proPackSecondary.includes('no_border') &&
    !proPackSecondary.includes('abd_pad');
  
  if (hasBorder) {
    // Exclude waterproof tape and conforming bandage if dressing has border
    return PROPACK_DEFAULT_ITEMS.filter(item => 
      !item.toLowerCase().includes('waterproof tape') && 
      !item.toLowerCase().includes('conforming bandage')
    );
  }
  
  return PROPACK_DEFAULT_ITEMS;
};

  return (
    <motion.div 
      className="px-4 sm:px-6 lg:px-12 pb-8"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div 
          className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 p-10 relative overflow-hidden"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-teal-400/10 to-cyan-400/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-emerald-400/10 to-green-400/10 rounded-full -ml-32 -mb-32 blur-3xl"></div>
          
          <div className="relative z-10">
            <motion.button 
              onClick={() => setOrderStep(2)}
              className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:gap-3 transition-all font-bold mb-6 group"
              whileHover={{ x: -5 }}
            >
              <IoArrowBack className="text-xl group-hover:scale-110 transition-transform" /> 
              Back
            </motion.button>

            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <motion.div 
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-lg ${
                        orderStep >= step 
                          ? 'bg-gradient-to-br from-teal-500 to-emerald-500 text-white shadow-lg' 
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                      }`}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: step * 0.1 }}
                      whileHover={{ scale: 1.1 }}
                    >
                      {orderStep > step ? <IoCheckmarkCircle className="text-2xl" /> : step}
                    </motion.div>
                    {step < 3 && (
                      <div className={`h-1 w-24 mx-2 rounded transition-all duration-500 ${
                        orderStep > step 
                          ? 'bg-gradient-to-r from-teal-500 to-emerald-500' 
                          : 'bg-gray-200 dark:bg-gray-700'
                      }`}></div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-xs font-bold text-gray-600 dark:text-gray-400">
                <span>Patient</span>
                <span>Wound Details</span>
                <span className="text-teal-600 dark:text-teal-400">Build ProPack</span>
              </div>
            </div>

            <motion.h2 
              className="text-3xl font-black text-gray-900 dark:text-white mb-2 bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Step 3 of 3 - Build Your ProPack
            </motion.h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">Customize your conservative care kit</p>

            <motion.div 
              className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-200 dark:border-teal-800 rounded-2xl p-6 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-lg font-black text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="text-2xl">📋</span>
                Order Summary
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white font-black text-lg">
                    {selectedPatient?.first_name?.charAt(0)}{selectedPatient?.last_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Patient</p>
                    <p className="text-sm font-black text-gray-900 dark:text-white">
                      {selectedPatient?.first_name} {selectedPatient?.last_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xl">🩹</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Wound Type</p>
                    <p className="text-sm font-black text-gray-900 dark:text-white">{woundDetails.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-xl p-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xl">💧</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase">Exudate Level</p>
                    <p className="text-sm font-black text-gray-900 dark:text-white">{woundDetails.drainage || 'Not Set'}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <KitDurationSelection kitDuration={kitDuration} setKitDuration={setKitDuration} />

            <motion.div 
  className="mb-8 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-2 border-emerald-300 dark:border-emerald-700 rounded-2xl p-6"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.2 }}
>
  <div className="flex items-center gap-2 mb-4">
    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-black">
      1
    </div>
    <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
      Primary Dressing
    </h3>
    <span className="text-red-500 text-xl">*</span>
  </div>

  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
          Select Primary Type <span className="text-red-500">*</span>
        </label>
        <select
          value={proPackPrimary}
          onChange={(e) => {
            setProPackPrimary(e.target.value);
            if (e.target.value === 'collagen_powder') {
              setProPackPrimarySize(''); // Reset size when switching to powder
            }
          }}
          disabled={!kitDuration}
          className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-emerald-300 dark:border-emerald-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold appearance-none cursor-pointer hover:border-emerald-400 dark:hover:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {!kitDuration ? 'Please select a pack duration first' : 'Select primary dressing...'}
          </option>
          {PROPACK_PRIMARY_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {!kitDuration && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
            ⚠️ Please select a pack duration above to enable dressing selection
          </p>
        )}
      </div>

      {/* Only show size for collagen_sheet in Primary section */}
      {proPackPrimary === 'collagen_sheet' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
            Size <span className="text-red-500">*</span>
          </label>
          <select
            value={proPackPrimarySize}
            onChange={(e) => setProPackPrimarySize(e.target.value)}
            disabled={!kitDuration}
            className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-emerald-300 dark:border-emerald-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold appearance-none cursor-pointer hover:border-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select size...</option>
            {PROPACK_SIZE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </motion.div>
      )}
    </div>

    <div className="flex items-center justify-center">
      {proPackPrimary === 'collagen_sheet' && (
        <img
          src={sheetimage}
          alt="Collagen Sheet"
          className="max-w-full max-h-48 object-contain rounded-lg shadow-md"
        />
      )}
      {proPackPrimary === 'collagen_powder' && (
        <img
          src={powderimage}
          alt="Collagen Powder"
          className="max-w-full max-h-48 object-contain rounded-lg shadow-md"
        />
      )}
      {!proPackPrimary && (
        <div className="w-full h-48 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center px-4">
            Select a primary dressing type to see product image
          </p>
        </div>
      )}
    </div>
  </div>
</motion.div>

{/* Secondary Dressing Section - Add size selection for collagen_powder */}
<motion.div 
  className="mb-8 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-2xl p-6"
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.3 }}
>
  <div className="flex items-center gap-2 mb-4">
    <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-black">
      2
    </div>
    <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
      Secondary Dressing
    </h3>
    <span className="text-red-500 text-xl">*</span>
  </div>

  <div className="mb-4 p-3 bg-purple-100 dark:bg-purple-900/30 border border-purple-200 dark:border-purple-800 rounded-xl">
    <p className="text-sm text-purple-800 dark:text-purple-300 font-medium flex items-center gap-2">
      <span className="text-lg">💧</span>
      <span>
        Configuration: <span className="font-black">{kitDuration || 'Not Set'}</span> · <span className="font-black">{woundDetails.drainage || 'Not Set'}</span> · <span className="font-black">{proPackPrimarySize ? `${proPackPrimarySize} primary` : 'No primary size'}</span>
      </span>
    </p>
  </div>

  <div className="space-y-4">
    {/* Size selection for collagen_powder - appears BEFORE secondary dressing selection */}
    {proPackPrimary === 'collagen_powder' && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
      >
        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
          Primary Dressing Size <span className="text-red-500">*</span>
        </label>
        <select
          value={proPackPrimarySize}
          onChange={(e) => setProPackPrimarySize(e.target.value)}
          disabled={!kitDuration}
          className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-purple-300 dark:border-purple-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold appearance-none cursor-pointer hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">Select primary size...</option>
          {PROPACK_SIZE_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </motion.div>
    )}

    <div>
      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
        Select Secondary Dressing <span className="text-red-500">*</span>
      </label>
      <select
        value={proPackSecondary}
        onChange={(e) => {
          const selectedOption = secondaryOptions.find(opt => opt.value === e.target.value);
          setProPackSecondary(e.target.value);
          setProPackSecondarySize(selectedOption?.productSize || '');
        }}
        disabled={secondaryOptions.length === 0}
        className="w-full px-5 py-4 bg-white dark:bg-gray-800 border-2 border-purple-300 dark:border-purple-600 rounded-xl text-gray-900 dark:text-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-semibold appearance-none cursor-pointer hover:border-purple-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <option value="">
          {secondaryOptions.length === 0
            ? 'Please complete pack duration, exudate level, and primary size first'
            : 'Select secondary dressing...'}
        </option>
        {secondaryOptions.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      {secondaryOptions.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 font-medium">
          ⚠️ Complete the following to see secondary dressing options: {!kitDuration && 'pack duration, '}
          {!woundDetails.drainage && 'exudate level (Step 2), '}
          {!proPackPrimarySize && 'primary size'}
        </p>
      )}
    </div>
  </div>
</motion.div>
            <motion.div 
              className="mb-8 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-black">
                  ✓
                </div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white uppercase">
                  Included with Every ProPack
                </h3>
              </div>
              <ul className="space-y-2">
                {getFilteredDefaultItems().map((item, index) => (
                  <motion.li
                    key={item}
                    className="flex items-center gap-3 text-gray-700 dark:text-gray-300 font-medium"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + (index * 0.05) }}
                  >
                    <span className="text-green-500 font-bold text-lg">✓</span>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="flex justify-between gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <motion.button 
                onClick={() => setOrderStep(2)}
                className="px-8 py-4 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-2xl font-bold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IoArrowBack />
                Back
              </motion.button>
              <motion.button 
                onClick={async () => {
                  if (!ivrSubmitted) {
                    setOpenIvrModal(true);
                  } else {
                    await fetchDeliveryAddress();
                    setDeliveryModalOpen(true);
                  }
                }}
                disabled={!canSubmit()}
                className={`px-8 py-4 rounded-2xl font-bold transition-all shadow-lg flex items-center gap-2 ${
                  canSubmit()
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white hover:shadow-xl'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
                }`}
                whileHover={canSubmit() ? { scale: 1.02 } : {}}
                whileTap={canSubmit() ? { scale: 0.98 } : {}}
              >
                {loading ? (
                  <>
                    <motion.div
                      className="w-5 h-5 border-3 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    Submitting...
                  </>
                ) : (
                  <>
                    <IoRocketOutline className="text-xl" />
                    Submit Order
                  </>
                )}
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ProPackBuilder;